import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Save, CheckCircle2, XCircle, Loader2, Lock, AlertCircle, RefreshCw } from "lucide-react";
import { AiBotSettings } from "../components/AiBotSettings";

export default function Settings() {
  const [s, setS] = useState(null);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [pw, setPw] = useState({
    current_password: "",
    new_password: "",
    confirm_password: "",
  });
  const [savingPw, setSavingPw] = useState(false);
  const [fixing, setFixing] = useState(false);
  const [fixResult, setFixResult] = useState(null);

  useEffect(() => {
    api.get("/settings").then((r) => setS(r.data));
  }, []);

  if (!s)
    return <div className="text-slate-500">Carregando...</div>;

  const save = async () => {
    try {
      await api.patch("/settings", s);
      toast.success("Configurações salvas!");
    } catch {
      toast.error("Erro ao salvar");
    }
  };

  const update = (k, v) => setS({ ...s, [k]: v });

  const testFocus = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      await api.patch("/settings", s);
      const { data } = await api.get("/fiscal/test-connection");
      setTestResult(data);
    } catch (e) {
      setTestResult({
        ok: false,
        detail: e?.response?.data?.detail || "Erro",
      });
    } finally {
      setTesting(false);
    }
  };

  const fixSequence = async () => {
    setFixing(true);
    setFixResult(null);
    try {
      await api.patch("/settings", s);
      const { data } = await api.post("/fiscal/fix-sequence");
      setFixResult(data);
      toast.success("Sequência ajustada. Tente emitir novamente.");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg =
        typeof detail === "string"
          ? detail
          : JSON.stringify(detail || "Erro").slice(0, 250);
      setFixResult({ message: msg, adjustments: [] });
      toast.error(msg);
    } finally {
      setFixing(false);
    }
  };

  const changePassword = async () => {
    if (pw.new_password.length < 6) {
      toast.error("A nova senha deve ter pelo menos 6 caracteres");
      return;
    }
    if (pw.new_password !== pw.confirm_password) {
      toast.error("A confirmação não confere com a nova senha");
      return;
    }
    if (pw.current_password === pw.new_password) {
      toast.error("A nova senha precisa ser diferente da atual");
      return;
    }
    setSavingPw(true);
    try {
      await api.post("/auth/change-password", {
        current_password: pw.current_password,
        new_password: pw.new_password,
      });
      toast.success("Senha alterada com sucesso 🔒");
      setPw({
        current_password: "",
        new_password: "",
        confirm_password: "",
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao alterar senha");
    } finally {
      setSavingPw(false);
    }
  };

  const Field = ({ k, label, type = "text", placeholder = "" }) => (
    <div>
      <label className="block text-xs uppercase tracking-wider text-slate-600 mb-1">
        {label}
      </label>
      <input
        type={type}
        value={s[k] ?? ""}
        onChange={(e) =>
          update(
            k,
            type === "number" ? Number(e.target.value) : e.target.value
          )
        }
        placeholder={placeholder}
        data-testid={`settings-${k}`}
        className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB]"
      />
    </div>
  );

  return (
    <div className="space-y-6" data-testid="settings-page">
      <header>
        <div className="text-xs uppercase tracking-[0.25em] text-[#2563EB] font-semibold mb-1">
          Sistema
        </div>
        <h1 className="font-display text-4xl font-bold">Configurações</h1>
      </header>

      <section className="bg-white border border-slate-200 rounded-sm p-6 space-y-4">
        <h2 className="font-display text-xl font-bold border-b border-slate-100 pb-2">
          Dados da Loja
        </h2>
        <div className="grid md:grid-cols-2 gap-4">
          <Field k="store_name" label="Nome da Loja" />
          <Field k="phone" label="Telefone Fixo" />
          <Field
            k="whatsapp_number"
            label="WhatsApp (ex: 5511999999999)"
          />
          <Field k="instagram_handle" label="@ Instagram (sem @)" />
          <div className="md:col-span-2">
            <Field k="address" label="Endereço" />
          </div>
          <Field k="pix_key" label="Chave PIX" />
          <Field k="store_logo_url" label="URL do Logo" />
          <div className="md:col-span-2">
            <Field
              k="site_url"
              label="URL pública do site (SEO — domínio final)"
              placeholder="https://upcel.com.br"
            />
          </div>
        </div>
        {s.store_logo_url && (
          <div className="flex items-center gap-3 pt-2">
            <span className="text-xs uppercase tracking-wider text-slate-500">
              Preview Logo
            </span>
            <img
              src={s.store_logo_url}
              alt="logo"
              className="w-16 h-16 object-cover rounded-sm border border-slate-200"
            />
          </div>
        )}
      </section>

      <AiBotSettings s={s} update={update} />

      {/* ========== SEGURANÇA / SENHA ========== */}
      <section
        className="bg-white border border-slate-200 rounded-sm p-6 space-y-4"
        data-testid="settings-password-section"
      >
        <div className="flex items-center gap-2 border-b border-slate-100 pb-2">
          <Lock size={18} className="text-[#2563EB]" />
          <h2 className="font-display text-xl font-bold">
            Segurança — Alterar Senha
          </h2>
        </div>
        <p className="text-sm text-slate-500">
          Recomendamos usar uma senha forte (mínimo 6 caracteres, misture
          letras, números e símbolos).
        </p>

        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <label className="block text-xs uppercase tracking-wider text-slate-600 mb-1">
              Senha atual
            </label>
            <input
              type="password"
              value={pw.current_password}
              onChange={(e) =>
                setPw({ ...pw, current_password: e.target.value })
              }
              placeholder="Digite sua senha atual"
              autoComplete="current-password"
              data-testid="settings-current-password"
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB]"
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-slate-600 mb-1">
              Nova senha
            </label>
            <input
              type="password"
              value={pw.new_password}
              onChange={(e) =>
                setPw({ ...pw, new_password: e.target.value })
              }
              placeholder="Mínimo 6 caracteres"
              autoComplete="new-password"
              data-testid="settings-new-password"
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB]"
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-slate-600 mb-1">
              Confirmar nova senha
            </label>
            <input
              type="password"
              value={pw.confirm_password}
              onChange={(e) =>
                setPw({ ...pw, confirm_password: e.target.value })
              }
              placeholder="Repita a nova senha"
              autoComplete="new-password"
              data-testid="settings-confirm-password"
              className={`w-full px-3 py-2 border rounded-sm text-sm focus:outline-none focus:ring-2 ${
                pw.confirm_password &&
                pw.new_password !== pw.confirm_password
                  ? "border-red-400 focus:ring-red-300"
                  : "border-slate-300 focus:ring-[#2563EB]"
              }`}
            />
            {pw.confirm_password &&
              pw.new_password !== pw.confirm_password && (
                <div className="text-[10px] text-red-500 mt-1">
                  As senhas não conferem
                </div>
              )}
          </div>
        </div>

        <button
          onClick={changePassword}
          disabled={
            savingPw ||
            !pw.current_password ||
            !pw.new_password ||
            !pw.confirm_password
          }
          data-testid="settings-change-password-btn"
          className="bg-[#0A1628] hover:bg-[#1e3a5f] disabled:opacity-50 text-white px-4 py-2 rounded-sm text-sm font-semibold flex items-center gap-2"
        >
          {savingPw ? (
            <Loader2 size={14} className="animate-spin" />
          ) : (
            <Lock size={14} />
          )}
          Alterar Senha
        </button>
      </section>

      {/* ========== FISCAL / FOCUS NFe ========== */}
      <section
        className="bg-white border border-slate-200 rounded-sm p-6 space-y-4"
        data-testid="settings-fiscal-section"
      >
        <div className="flex items-center justify-between border-b border-slate-100 pb-2">
          <h2 className="font-display text-xl font-bold">
            Fiscal — NF-e / NFC-e (Focus NFe)
          </h2>
          <span className="text-[10px] uppercase tracking-wider bg-amber-100 text-amber-700 px-2 py-1 rounded-sm">
            Homologação / Produção
          </span>
        </div>
        <p className="text-sm text-slate-500">
          Emita Nota Fiscal Eletrônica (NF-e) e Nota Fiscal de Consumidor
          (NFC-e) via Focus NFe. O certificado digital A1 (.pfx) deve ser
          enviado diretamente no painel da Focus NFe
          (<a
            href="https://app-v2.focusnfe.com.br"
            target="_blank"
            rel="noreferrer"
            className="text-[#2563EB] underline"
          >
            app-v2.focusnfe.com.br
          </a>
          ) → Serviços → Minhas Empresas → Anexar Certificado.
        </p>

        <div className="grid md:grid-cols-2 gap-4">
          <Field k="fiscal_cnpj" label="CNPJ (apenas números)" />
          <Field k="fiscal_ie" label="Inscrição Estadual" />
          <div className="md:col-span-2">
            <Field k="fiscal_razao_social" label="Razão Social" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider text-slate-600 mb-1">
              Regime Tributário
            </label>
            <select
              value={s.fiscal_regime || 1}
              onChange={(e) =>
                update("fiscal_regime", Number(e.target.value))
              }
              data-testid="settings-fiscal_regime"
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
            >
              <option value={1}>1 — Simples Nacional</option>
              <option value={2}>2 — Simples Nacional excesso sublim.</option>
              <option value={3}>3 — Regime Normal</option>
            </select>
          </div>
          <Field
            k="fiscal_csosn"
            label="CSOSN padrão (Simples)"
            placeholder="ex: 102"
          />
          <Field
            k="fiscal_ncm_padrao"
            label="NCM padrão (se produto não tiver)"
            placeholder="ex: 85171231"
          />
          <Field k="fiscal_uf" label="UF" placeholder="SP" />
          <Field k="fiscal_municipio_nome" label="Município (nome)" />
          <Field
            k="fiscal_ibge_municipio"
            label="Código IBGE do Município"
            placeholder="ex: 4204202"
          />
          <Field k="fiscal_logradouro" label="Logradouro" />
          <Field k="fiscal_numero" label="Número" />
          <Field k="fiscal_bairro" label="Bairro" />
          <Field k="fiscal_cep" label="CEP (apenas números)" />
        </div>

        <div className="grid md:grid-cols-2 gap-4 pt-2 border-t border-slate-100">
          <div>
            <label className="block text-xs uppercase tracking-wider text-slate-600 mb-1">
              Ambiente Focus NFe
            </label>
            <select
              value={s.focus_environment || "homologacao"}
              onChange={(e) =>
                update("focus_environment", e.target.value)
              }
              data-testid="settings-focus_environment"
              className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
            >
              <option value="homologacao">Homologação (testes)</option>
              <option value="producao">Produção (emite nota real)</option>
            </select>
          </div>
          <div className="md:col-span-2">
            <Field
              k="focus_token"
              label="Token Focus NFe"
              placeholder="cole aqui o token gerado no painel Focus"
            />
          </div>
        </div>

        <div className="flex items-center gap-3 pt-2">
          <button
            onClick={testFocus}
            disabled={testing || !s.focus_token}
            data-testid="settings-fiscal-test-btn"
            className="bg-[#0A1628] hover:bg-[#1e3a5f] disabled:opacity-50 text-white px-4 py-2 rounded-sm text-sm font-semibold flex items-center gap-2"
          >
            {testing ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <CheckCircle2 size={14} />
            )}
            Testar conexão Focus
          </button>

          {testResult && (
            <div
              className={`text-sm ${
                testResult.ok ? "text-emerald-700" : "text-red-700"
              } space-y-2 w-full`}
              data-testid="settings-fiscal-test-result"
            >
              <div className="flex items-center gap-2 font-semibold">
                {testResult.ok ? (
                  <CheckCircle2 size={16} className="text-emerald-600" />
                ) : (
                  <XCircle size={16} className="text-red-600" />
                )}
                <span>{testResult.message || testResult.detail}</span>
              </div>
              {testResult.diagnostic && (
                <div className="bg-slate-50 border border-slate-200 rounded-sm p-2 text-[11px] font-mono text-slate-700 grid grid-cols-1 md:grid-cols-2 gap-x-4">
                  <div>Ambiente config: <b>{testResult.diagnostic.environment_setting}</b></div>
                  <div>URL Focus usada: <b>{testResult.diagnostic.focus_url}</b></div>
                  <div>CNPJ: <b>{testResult.diagnostic.cnpj}</b></div>
                  <div>Token: <b>{testResult.diagnostic.token_preview}</b> ({testResult.diagnostic.token_length} chars)</div>
                  {testResult.diagnostic.token_had_whitespace && (
                    <div className="col-span-2 text-amber-700 font-bold">
                      ⚠️ Token tinha espaços/quebras de linha — o sistema removeu automaticamente ao usar.
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Fix sequence NFC-e */}
        <div className="pt-4 border-t border-slate-100">
          <div className="bg-amber-50 border border-amber-200 rounded-sm p-4">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-sm bg-amber-500 text-white flex items-center justify-center flex-shrink-0">
                <AlertCircle size={18} />
              </div>
              <div className="flex-1">
                <div className="text-xs uppercase tracking-[0.2em] text-amber-800 font-bold mb-1">
                  Correção de Sequência NFC-e / NF-e
                </div>
                <p className="text-sm text-amber-900 mb-3">
                  Use este botão se o SEFAZ estiver rejeitando com{" "}
                  <b>&quot;Duplicidade de NF-e&quot;</b> (código 539). O sistema consulta
                  o Focus NFe, detecta o maior número já usado no seu CNPJ e
                  ajusta o próximo número com margem de segurança (+5).
                </p>
                <button
                  onClick={fixSequence}
                  disabled={fixing || !s.focus_token}
                  data-testid="settings-fix-sequence-btn"
                  className="bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white px-4 py-2 rounded-sm text-sm font-semibold flex items-center gap-2"
                >
                  {fixing ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <RefreshCw size={14} />
                  )}
                  Corrigir sequência agora
                </button>
                {fixResult && (
                  <div
                    className="mt-3 text-sm bg-white border border-amber-200 rounded-sm p-3"
                    data-testid="settings-fix-sequence-result"
                  >
                    <div className="font-semibold mb-1">
                      {fixResult.message}
                    </div>
                    {fixResult.adjustments?.length > 0 && (
                      <table className="w-full text-xs mt-2">
                        <thead>
                          <tr className="text-slate-500">
                            <th className="text-left">Doc</th>
                            <th className="text-left">Séries</th>
                            <th className="text-right">Nº visto</th>
                            <th className="text-right">Novo Focus</th>
                            <th className="text-center">Ação</th>
                          </tr>
                        </thead>
                        <tbody>
                          {fixResult.adjustments.map((a, i) => (
                            <tr key={i} className="border-t border-slate-100">
                              <td className="py-1 uppercase font-mono">{a.doc_type}</td>
                              <td>{(a.series_seen || [a.serie]).join(", ")}</td>
                              <td className="text-right">{a.max_seen}</td>
                              <td className="text-right font-semibold">{a.new_focus ?? "—"}</td>
                              <td className="text-center">
                                {a.action === "updated" ? (
                                  <span className="text-emerald-600 font-semibold">✓ OK</span>
                                ) : (
                                  <span className="text-red-600" title={a.error}>
                                    erro
                                  </span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white border border-slate-200 rounded-sm p-6 space-y-4">
        <h2 className="font-display text-xl font-bold border-b border-slate-100 pb-2">
          Integração Meta (Opcional)
        </h2>
        <p className="text-sm text-slate-500">
          Preencha para ativar integração oficial WhatsApp Cloud API e
          Instagram Graph API. Sem isso, o sistema usa links rápidos do
          wa.me e instagram.com.
        </p>
        <div className="grid md:grid-cols-2 gap-4">
          <Field k="meta_phone_number_id" label="Phone Number ID" />
          <Field k="meta_ig_business_id" label="Instagram Business ID" />
          <div className="md:col-span-2">
            <Field k="meta_access_token" label="Access Token" />
          </div>
        </div>
      </section>

      <button
        onClick={save}
        data-testid="settings-save-btn"
        className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white px-5 py-2.5 rounded-sm font-semibold text-sm flex items-center gap-2"
      >
        <Save size={16} /> Salvar Configurações
      </button>
    </div>
  );
}
